import { Plan } from '@app/shared/models/plan';
import { Subscription } from '@app/shared/models/subscription';
import { Action, createReducer, on } from '@ngrx/store';
import { GetStripePaymentMethodOutput } from '@smash-sdk/billing/ejs/01-2024/types/GetStripePaymentMethod/GetStripePaymentMethod';
import { UserBillingActions } from '../actions';


export const billingFeatureKey = 'billing';

export interface State {
  invoices: any[];
  invoicesLoading: boolean;
  subscription: Subscription;
  plan: Plan;
  computedPlan: any;
  planPriceComputing: boolean;
  planPriceComputingError: any;
  subscriptionLoaded: boolean;

  unsubscribeProcessing: boolean;
  unsubscribeComplete: boolean;
  unsubscribeError: any;

  billingInformationsUpdating: boolean;
  billingInformationsUpdated: boolean;
  billingInformationsUpdatingError: any;

  paymentMethod: GetStripePaymentMethodOutput['paymentMethod'];
  paymentMethodLoading: boolean;
  paymentMethodError: string;
  paymentMethodUpdated: boolean;
  provider: string;
}

export const initialState: State = {
  invoices: [],
  invoicesLoading: false,
  subscription: null,
  plan: null,
  computedPlan: null,
  planPriceComputing: false,
  planPriceComputingError: null,
  subscriptionLoaded: false,

  unsubscribeProcessing: false,
  unsubscribeComplete: false,
  unsubscribeError: null,

  billingInformationsUpdating: false,
  billingInformationsUpdated: false,
  billingInformationsUpdatingError: null,
  paymentMethod: null,
  paymentMethodLoading: false,
  paymentMethodUpdated: null,
  paymentMethodError: null,
  provider: null,
};

const billingReducer = createReducer(
  initialState,
  on(UserBillingActions.loadInvoices, (state) => ({
    ...state,
    invoicesLoading: true
  })),
  on(UserBillingActions.loadInvoicesSuccess, (state, { invoices }) => ({
    ...state,
    invoices,
    invoicesLoading: false
  })),
  on(UserBillingActions.loadInvoicesFailure, (state, { error }) => ({
    ...state,
    error,
    invoicesLoading: false
  })),
  on(UserBillingActions.loadSubscriptionAndPlan, (state) => ({
    ...state,
    subscription: initialState.subscription,
    plan: initialState.plan,
    subscriptionLoaded: initialState.subscriptionLoaded,
  })),
  on(UserBillingActions.loadSubscriptionAndPlanSuccess, (state, { subscription, plan }) => ({
    ...state,
    subscription,
    plan,
    subscriptionLoaded: true
  })),
  on(UserBillingActions.loadSubscriptionAndPlanFailure, (state, { error }) => ({
    ...state,
    error,
    subscriptionLoadedError: error
  })),
  on(UserBillingActions.unsubscribe, (state) => ({
    ...state,
    unsubscribeProcessing: true,
    unsubscribeComplete: initialState.unsubscribeComplete,
    unsubscribeError: initialState.unsubscribeError,
  })),
  on(UserBillingActions.unsubscribeSuccess, (state, { subscription }) => ({
    ...state,
    subscription,
    unsubscribeProcessing: false,
    unsubscribeComplete: true,
  })),
  on(UserBillingActions.unsubscribeFailure, (state, { error }) => ({
    ...state,
    unsubscribeProcessing: false,
    unsubscribeComplete: false,
    unsubscribeError: error,
  })),
  on(UserBillingActions.UpdateBillingInformations, (state) => ({
    ...state,
    billingInformationsUpdating: true,
    billingInformationsUpdated: initialState.billingInformationsUpdated,
    billingInformationsUpdatingError: initialState.billingInformationsUpdatingError,
  })),
  on(UserBillingActions.UpdateBillingInformationsSuccess, (state, { billingInformations }) => ({
    ...state,
    // billingInformations, // useless
    billingInformationsUpdating: false,
    billingInformationsUpdated: true,
  })),
  on(UserBillingActions.UpdateBillingInformationsFailure, (state, { error }) => ({
    ...state,
    billingInformationsUpdating: false,
    billingInformationsUpdatingError: error,
  })),
  // Compute Plan
  on(UserBillingActions.computePlan, (state) => ({
    ...state,
    computedPlan: initialState.computedPlan,
    planPriceComputingError: initialState.planPriceComputingError,
    planPriceComputing: true,
  })),
  on(UserBillingActions.computePlanSuccess, (state, { computedPlan }) => ({
    ...state,
    computedPlan,
    planPriceComputing: false,
  })),
  on(UserBillingActions.computePlanFailure, (state, { error }) => ({
    ...state,
    planPriceComputingError: error,
    planPriceComputing: false,
  })),

  on(UserBillingActions.retrieveStripePaymentMethod, UserBillingActions.updateStripePaymentMethod, (state) => ({
    ...state,
    paymentMethodLoading: true,
    paymentMethodUpdated: false,
    paymentMethodError: null,
  })),
  on(UserBillingActions.retrieveStripePaymentMethodSuccess, (state, { paymentMethod }) => ({
    ...state,
    paymentMethod,
    paymentMethodLoading: false,
    paymentMethodError: null,
  })),
  on(UserBillingActions.updateStripePaymentMethodSuccess, (state) => ({
    ...state,
    paymentMethodLoading: false,
    paymentMethodError: null,
    paymentMethodUpdated: true,
  })),
  on(UserBillingActions.retrieveStripePaymentMethodError, UserBillingActions.updateStripePaymentMethodError, (state, { error }) => ({
    ...state,
    paymentMethodLoading: false,
    paymentMethodError: error,
    paymentMethodUpdated: false,
  })),
  on(UserBillingActions.resetStripePaymentMethodError, (state) => ({
    ...state,
    paymentMethodError: null,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return billingReducer(state, action);
}
